import type { LandingLocale } from '../../shared/content/landingContentTypes.js';
import { resolveLandingDocumentSeo } from '../../shared/landingDocumentSeo.js';
import { landingWebPageSchema, landingBreadcrumbSchema } from '../../shared/landingDocumentSchema.js';

const setMeta = (key: string, value: string, property = false) => {
  const attribute = property ? 'property' : 'name';
  let node = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);
  if (!node) {
    node = document.createElement('meta');
    node.setAttribute(attribute, key);
    document.head.append(node);
  }
  node.content = value;
};

/** Update the same document identity after SPA navigation that the server sends on a direct visit. */
export const syncLandingDocumentMetadata = (locale: LandingLocale, description?: string) => {
  const currentCanonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')?.href;
  const identity = resolveLandingDocumentSeo(location.origin, location.pathname, locale);
  const fallback = description ?? (currentCanonical === identity.canonicalUrl
    ? document.head.querySelector<HTMLMetaElement>('meta[name="description"]')?.content : '');
  const seo = resolveLandingDocumentSeo(location.origin, location.pathname, locale, fallback);
  document.documentElement.lang = locale;
  if (!seo.publicPage) {
    setMeta('robots', 'noindex,nofollow');
    document.head.querySelectorAll('link[rel="canonical"], link[hreflang]').forEach(node => node.remove());
    return;
  }
  document.title = seo.title;
  setMeta('title', seo.title);
  setMeta('description', seo.description);
  setMeta('robots', document.documentElement.dataset.vpPublicIndexing === 'true'
    ? 'index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1' : 'noindex,follow');
  let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  const previousCanonical = canonical?.href;
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.rel = 'canonical';
    document.head.append(canonical);
  }
  canonical.href = seo.canonicalUrl;
  for (const alternate of seo.alternates) {
    let node = document.head.querySelector<HTMLLinkElement>(`link[rel="alternate"][hreflang="${alternate.language}"]`);
    if (!node) {
      node = document.createElement('link');
      node.rel = 'alternate';
      node.hreflang = alternate.language;
      document.head.append(node);
    }
    node.href = alternate.href;
  }
  for (const [key, value] of Object.entries({
    'og:title': seo.title, 'og:description': seo.description, 'og:url': seo.canonicalUrl,
    'og:locale': locale === 'de' ? 'de_DE' : locale === 'tr' ? 'tr_TR' : 'en_US',
  })) setMeta(key, value, true);
  document.head.querySelectorAll('meta[property="og:locale:alternate"]').forEach(node => node.remove());
  for (const language of ['de', 'en', 'tr']) {
    if (language === locale) continue;
    const node = document.createElement('meta');
    node.setAttribute('property', 'og:locale:alternate');
    node.content = `${language}_${language === 'en' ? 'US' : language.toUpperCase()}`;
    document.head.append(node);
  }
  setMeta('twitter:title', seo.title);
  setMeta('twitter:description', seo.description);
  // Retain organization data, but never leave the previous page's identity/FAQ in the new document.
  for (const script of document.head.querySelectorAll<HTMLScriptElement>('script[type="application/ld+json"]')) {
    try {
      const data: { '@graph'?: Array<Record<string, unknown>> } = JSON.parse(script.textContent ?? '{}');
      if (!Array.isArray(data['@graph'])) continue;
      const graph = data['@graph'].filter(node => !['WebPage', 'ContactPage', 'AboutPage', 'BreadcrumbList'].includes(String(node['@type'])) &&
        (node['@type'] !== 'FAQPage' || previousCanonical === seo.canonicalUrl));
      graph.push(landingWebPageSchema(seo.canonicalUrl, seo.title, seo.description, locale, location.pathname));
      const breadcrumb = landingBreadcrumbSchema(seo.canonicalUrl, seo.title, locale, location.pathname);
      if (breadcrumb) graph.push(breadcrumb);
      script.textContent = JSON.stringify({ ...data, '@graph': graph }).replace(/</gu, '\\u003c');
    } catch { /* Leave independently supplied structured data intact. */ }
  }
};
